import {
    CognitoIdentityProviderClient,
    CognitoIdentityProvider,
} from "@aws-sdk/client-cognito-identity-provider"
import { DynamoDBClient, DynamoDBClientConfig } from "@aws-sdk/client-dynamodb"
import { S3Client } from "@aws-sdk/client-s3"
import { DynamoDBDocument } from "@aws-sdk/lib-dynamodb"
import { SupportedRegions } from "@nova/constants"
import {
    CognitoJwtVerifier,
    CognitoJwtVerifierSingleUserPool,
} from "aws-jwt-verify/cognito-verifier"
import { jwksCache } from "./jwks.server"
import { config } from "@nova/configuration/node"
import { SecretsManagerClient } from "@aws-sdk/client-secrets-manager"
import { SFNClient } from "@aws-sdk/client-sfn"

export type { CognitoJwtPayload } from "aws-jwt-verify/jwt-model"

type RegionMap<T> = Partial<Record<SupportedRegions, T>>

type ClientTypes = {
    s3: S3Client
    dynamo: DynamoDBClient
    dynamoDocument: DynamoDBDocument
    cognitoClient: CognitoIdentityProviderClient
    cognito: CognitoIdentityProvider
    jwtVerifier: CognitoJwtVerifierSingleUserPool<{ userPoolId: string }>
    secrets: SecretsManagerClient
    stepFunction: SFNClient
}

type ClientKey = keyof ClientTypes

type ClientCache = Partial<Record<ClientKey, RegionMap<unknown>>>

type GlobalWithClients = typeof globalThis & {
    __novaClientCache__?: ClientCache
}

const g = ((globalThis as GlobalWithClients).__novaClientCache__ ??= {} as ClientCache)
type ClientValue<K extends ClientKey> = ClientTypes[K]

const ensureRegistry = <K extends ClientKey>(key: K): RegionMap<ClientValue<K>> => {
    const existing = g[key] as RegionMap<ClientValue<K>> | undefined
    if (existing) {
        return existing
    }

    const created: RegionMap<ClientValue<K>> = {}
    g[key] = created as RegionMap<unknown>
    return created
}

export const getGenericClient =
    <K extends ClientKey>(
        clientType: K,
        Client: new (config: { region: SupportedRegions }) => ClientValue<K>
    ) =>
    (
        region = (process.env.AWS_REGION as SupportedRegions) || SupportedRegions.Canada
    ): ClientValue<K> => {
        const registry = ensureRegistry(clientType)

        const existing = registry[region]
        if (existing) return existing

        const client = new Client({ region })
        registry[region] = client
        return client
    }

export const getCanadaClient =
    <K extends ClientKey>(
        clientType: K,
        Client: new (config: { region: SupportedRegions }) => ClientValue<K>
    ) =>
    (): ClientValue<K> => {
        const region = SupportedRegions.Canada
        const registry = ensureRegistry(clientType)

        const existing = registry[region]
        if (existing) return existing

        const client = new Client({ region })
        registry[region] = client
        return client
    }

export const getS3Client = getGenericClient("s3", S3Client)

export const getDynamoClient = (
    region = (process.env.AWS_REGION as SupportedRegions) || SupportedRegions.Canada
): DynamoDBClient => {
    const registry = ensureRegistry("dynamo")

    const existing = registry[region] as DynamoDBClient | undefined
    if (existing) return existing

    const clientConfig: DynamoDBClientConfig = { region }

    const endpoint = process.env.DYNAMODB_ENDPOINT
    if (endpoint) {
        clientConfig.endpoint = endpoint
        clientConfig.credentials = {
            accessKeyId: process.env.AWS_ACCESS_KEY_ID ?? "local",
            secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY ?? "local",
        }
    }

    const client = new DynamoDBClient(clientConfig)
    registry[region] = client
    return client
}

export const getDynamoDocument = (
    region = (process.env.AWS_REGION as SupportedRegions) || SupportedRegions.Canada
) => {
    const registry = ensureRegistry("dynamoDocument")

    const existing = registry[region]
    if (existing) return existing

    const client = DynamoDBDocument.from(getDynamoClient(region))
    registry[region] = client
    return client
}

export const getCognitoClient = getCanadaClient("cognitoClient", CognitoIdentityProviderClient)

export const getJWTVerifier = () => {
    const region = SupportedRegions.Canada
    const registry = ensureRegistry("jwtVerifier")

    const existing = registry[region]
    if (existing) return existing
    const client = CognitoJwtVerifier.create({
        userPoolId: config.get("awsUserPoolId"),
    })

    // @todo doug - revisit this after launch to determine if this is still a good idea
    client.cacheJwks(jwksCache)
    registry[region] = client
    return client
}

export const getCognito = getCanadaClient("cognito", CognitoIdentityProvider)

export const getSecretsClient = getGenericClient("secrets", SecretsManagerClient)

export const getStepFunctionClient = getGenericClient("stepFunction", SFNClient)
